import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, FormControlLabel, Grid, Radio, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AssessmentCategory } from '../../../../Models/Assessment/AssessmentCategory.model';
import { AssessmentTypeEnum } from '../../../../Enum/AssessmentTypeEnum';
import UploadPhotoDialog from './UploadPhotoDialog';
import { RootState } from '../../../../reducers';
import { useSelector } from 'react-redux';
import { handleOpenUploadPhoto, setShowUploadPhoto } from '../../../../store/assessment.slice';
import { useAppDispatch } from '../../../../store/store';

export type RenderCategoryQuestions = (args: {
    category: AssessmentCategory;
    assessmentType: AssessmentTypeEnum;
    memberId: string;
    onChange: (updatedCategory: AssessmentCategory) => void;
}) => React.ReactNode;

interface AssessmentCategoryListProps {
    memberId: string;
    assessment: any;
    assessmentType: AssessmentTypeEnum;
    expandedCategoryByTab: Partial<Record<AssessmentTypeEnum, string | null>>;
    handleCategoryToggle: (categoryName: string) => void;
    getCurrentMasterSelection: (category: AssessmentCategory) => string | null;
    getMasterRadioOptions: (category: AssessmentCategory) => { value: string; label: string }[];
    handleMasterRadioChange: (category: AssessmentCategory, value: string, e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    handleOnChangeCategory: (category: AssessmentCategory) => void;
    renderCategoryQuestions: RenderCategoryQuestions;
}

const AssessmentCategoryList: React.FC<AssessmentCategoryListProps> = React.memo(
    ({
        memberId,
        assessment,
        assessmentType,
        expandedCategoryByTab,
        handleCategoryToggle,
        getCurrentMasterSelection,
        getMasterRadioOptions,
        handleMasterRadioChange,
        handleOnChangeCategory,
        renderCategoryQuestions,
    }) => {
        const dispatch = useAppDispatch();
        const { photoFileName, showUploadPhoto } = useSelector((state: RootState) => state.assessment);

        return (
            <>
                <UploadPhotoDialog open={showUploadPhoto} onClose={() => dispatch(setShowUploadPhoto(false))} />
                {assessment?.Assessment?.Categories?.map((category: AssessmentCategory, index: number) => {
                    if (!category) return null;

                    const questionCount = category.Questions ? category.Questions.length : 0;
                    const answeredCount = category.Questions
                        ? category.Questions.filter((q) => q.Options && q.Options.some((opt) => opt.IsSelected)).length
                        : 0;
                    const isExpanded = expandedCategoryByTab[assessmentType] === category.Name || expandedCategoryByTab[assessmentType] === 'All';
                    const masterOptions = getMasterRadioOptions(category);
                    const currentSelection = getCurrentMasterSelection(category);

                    return (
                        <Grid size={12} key={`category_${category.Name}_${index}`}>
                            <Accordion
                                expanded={isExpanded}
                                onChange={() => handleCategoryToggle(category.Name)}
                                elevation={2}
                                sx={{ width: '100%' }}
                                TransitionProps={{ unmountOnExit: true }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls={`${category.Name}-content`}
                                    id={`${category.Name}-header`}
                                    sx={{ backgroundColor: '#f5f5f5' }}
                                >
                                    <Stack
                                        spacing={{ xs: 1, sm: 2 }}
                                        direction={{ xs: 'column', sm: 'row' }}
                                        sx={{
                                            width: '100%',
                                            pr: { xs: 0, md: 1 },
                                            alignItems: { xs: 'flex-start', sm: 'center' },
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant="body1" color="primary">
                                                {category.Name}
                                            </Typography>
                                            <Typography variant="body2" color="primary">
                                                ({answeredCount}/{questionCount})
                                            </Typography>
                                        </Stack>

                                        {masterOptions.length > 0 && (
                                            <Box
                                                onClick={(e) => e.stopPropagation()}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    backgroundColor: 'rgba(255,255,255,0.8)',
                                                    borderRadius: 1,
                                                    px: 1,
                                                    py: 0.5,
                                                    border: '1px solid #e0e0e0',
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <Typography variant="caption" color="textSecondary">
                                                    All:
                                                </Typography>
                                                {masterOptions.map((option) => (
                                                    <FormControlLabel
                                                        key={`master-${category.Name}-${option.value}`}
                                                        control={
                                                            <Radio
                                                                size="small"
                                                                name={`master-${category.Name}`}
                                                                value={option.value}
                                                                checked={currentSelection === option.value}
                                                                onChange={() => {}}
                                                                onClick={(e) => handleMasterRadioChange(category, option.value, e)}
                                                            />
                                                        }
                                                        label={option.label}
                                                        sx={{
                                                            margin: 0,
                                                            mr: 0.5,
                                                            '& .MuiFormControlLabel-label': {
                                                                fontSize: '0.8rem',
                                                                fontWeight: 500,
                                                            },
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                    </Stack>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {category.Name === 'Skin' && (
                                        <Stack direction={'row'} justifyContent={'space-between'} sx={{ mt: 4, borderBottom: '1px solid #e0e0e0', pb: 4 }}>
                                            <Stack>
                                                <Typography variant="subtitle2" sx={{ mb: 1, pl: 2 }}>
                                                    Upload skin photo to member documents:
                                                </Typography>
                                                {photoFileName && (
                                                    <Typography variant="subtitle1" fontStyle={'italic'} sx={{ pl: 2 }}>
                                                        Selected file: {photoFileName}
                                                    </Typography>
                                                )}
                                            </Stack>
                                            <Button variant="contained" sx={{ mt: 2, textAlign: 'center' }} onClick={() => dispatch(handleOpenUploadPhoto())}>
                                                Upload photo
                                            </Button>
                                        </Stack>
                                    )}
                                    {renderCategoryQuestions({
                                        category,
                                        assessmentType,
                                        memberId,
                                        onChange: (updatedCategory: AssessmentCategory) => handleOnChangeCategory(updatedCategory),
                                    })}
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                    );
                })}
            </>
        );
    }
);

export default AssessmentCategoryList;
